import React, { useState, useEffect, useRef } from 'react';
import { API_URL, LOCATION_ID } from '..';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const AddStockLayer = () => {
    const [formValues, setFormValues] = useState({
        title: '',
        sellPrice: 0,
        costPrice: 0,
        quantity: 0,
        description: '',
        locationId:LOCATION_ID
    });
    const [message, setMessage] = useState({
        status: false,
        reason: '',
        desc: ''
    });
    const quillRef = useRef(null);
    const quillInstance = useRef(null);

    // Initialize Quill editor
    useEffect(() => {
        if (quillRef.current && !quillInstance.current) {
            quillInstance.current = new Quill(quillRef.current, {
                theme: 'snow',
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ align: '' }, { align: 'center' }, { align: 'right' }],
                    ],
                },
                placeholder: 'Enter stock description',
            });

            // Set text direction to LTR and language to English
            quillInstance.current.root.setAttribute('dir', 'ltr');
            quillInstance.current.root.setAttribute('lang', 'en');

            // Handle text changes and focus
            quillInstance.current.on('text-change', () => {
                const content = quillInstance.current.root.innerHTML;
                setFormValues((prev) => ({ ...prev, description: content }));
                quillInstance.current.focus();
            });

            // Focus the editor on initialization
            quillInstance.current.focus();
        }

        // Cleanup on unmount
        return () => {
            if (quillInstance.current) {
                quillInstance.current.off('text-change');
                quillInstance.current = null;
            }
        };
    }, []);

    // Update Quill content when description changes
    useEffect(() => {
        if (quillInstance.current && formValues.description) {
            if (quillInstance.current.root.innerHTML !== formValues.description) {
                quillInstance.current.root.innerHTML = formValues.description;
                quillInstance.current.focus();
            }
        }
    }, [formValues.description]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) {
            setMessage({ status: true, reason: 'error', desc: 'User not authenticated. Please sign in.' });
            return;
        }

        // Validation
        if (!formValues.title) {
            setMessage({ status: true, reason: 'error', desc: 'Title is required' });
            return;
        }
        if (!formValues.costPrice || formValues.costPrice <= 0) {
            setMessage({ status: true, reason: 'error', desc: 'Buy Price must be greater than 0' });
            return;
        }
        if (!formValues.sellPrice || formValues.sellPrice <= 0) {
            setMessage({ status: true, reason: 'error', desc: 'Selling Price must be greater than 0' });
            return;
        }
        if (!formValues.quantity || formValues.quantity <= 0) {
            setMessage({ status: true, reason: 'error', desc: 'Quantity must be greater than 0' });
            return;
        }
        if (!formValues.description || formValues.description === '<p><br></p>') {
            setMessage({ status: true, reason: 'error', desc: 'Description is required' });
            return;
        }

        try {
            let headersList = {
                "Accept": "*/*",
                "Content-Type": "application/json"
            };
            let response = await fetch(`${API_URL}/api/invoice/stock/create`, {
                method: "POST",
                headers: headersList,
                body: JSON.stringify(formValues)
            });
            const data = await response.json();

            if (response.status === 201 || response.status === 200) {
                setMessage({ status: true, reason: 'success', desc: 'Stock added successfully' });
                setFormValues({
                    title: '',
                    sellPrice: 0,
                    costPrice: 0,
                    quantity: 0,
                    description: ''
                });
                if (quillInstance.current) {
                    quillInstance.current.root.innerHTML = '';
                    quillInstance.current.focus();
                }
            } else {
                throw new Error(data.message || 'Failed to add stock');
            }
        } catch (error) {
            setMessage({ status: true, reason: 'error', desc: `Failed to add stock: ${error.message}` });
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <style>
                    {`
                        .ql-container {
                            font-family: Arial, sans-serif;
                            font-size: 14px;
                            min-height: 150px;
                            border: 1px solid #ced4da;
                            border-radius: 8px;
                            direction: ltr;
                        }
                        .ql-container.is-invalid {
                            border-color: #dc3545;
                        }
                        .ql-editor {
                            min-height: 120px;
                            padding: 10px;
                        }
                        .ql-toolbar {
                            border-bottom: 1px solid #ced4da;
                            border-top-left-radius: 8px;
                            border-top-right-radius: 8px;
                        }
                        .ql-editor:focus {
                            outline: none;
                            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
                        }
                    `}
                </style>
                <div className="row justify-content-center">
                    <div className="col-xxl-6 col-xl-8 col-lg-10">
                        <div className="card border">
                            <div className="card-body">
                                {/* Message Alert */}
                                {message.status && (
                                    <div className={`alert alert-${message.reason === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-20`} role="alert">
                                        {message.desc}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setMessage({ ...message, status: false })}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                )}

                                {/* Form */}
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="title"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Title <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="title"
                                            value={formValues.title}
                                            onChange={(e) => setFormValues((prev) => ({ ...prev, title: e.target.value }))}
                                            placeholder="Title...."
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="buy"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Buy Price <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control radius-8"
                                            id="buy"
                                            value={formValues.costPrice}
                                            onChange={(e) => setFormValues((prev) => ({ ...prev, costPrice: e.target.value }))}
                                            placeholder="0.00"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="sell"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Selling Price <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control radius-8"
                                            id="sell"
                                            value={formValues.sellPrice}
                                            onChange={(e) => setFormValues((prev) => ({ ...prev, sellPrice: e.target.value }))}
                                            placeholder="0.00"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="qty"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Quantity <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control radius-8"
                                            id="qty"
                                            value={formValues.quantity}
                                            onChange={(e) => setFormValues((prev) => ({ ...prev, quantity: e.target.value }))}
                                            min="1"
                                            placeholder="Enter Quantity"
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="description"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Description <span className="text-danger-600">*</span>
                                        </label>
                                        <div
                                            ref={quillRef}
                                            className={`ql-container ${message.reason === 'error' && (!formValues.description || formValues.description === '<p><br></p>') ? 'is-invalid' : ''}`}
                                        />
                                        <div className="form-text">Provide a detailed description of the stock item (supports bold, bullet points, etc.).</div>
                                        {message.reason === 'error' && (!formValues.description || formValues.description === '<p><br></p>') && (
                                            <div style={{ color: '#dc3545', fontSize: '0.875em' }}>Description is required</div>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <button
                                            type="submit"
                                            className="btn btn-primary border border-primary-400 text-md px-56 py-12 radius-8"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddStockLayer;